import {
  AppointmentCard,
  EmptySectionCard,
  InquiryDetailSectionHeader,
  SkeletonLoader,
  Text,
  ChakraVStack,
  ChakraHStack,
  FireflyButton,
  FAIconName,
  Icon,
} from '@equip.health/ui';
import { AppointmentTypeName } from '~/lib/constants/scheduling.constants';

import { Section } from '~/components/common/Section';
import { AppointmentCardDisplayProps } from '~/lib/util/appointment.util';
import { getTooltipWrapper } from '../viewUtil';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { useMemo, useState } from 'react';
import AdmissionConsultationModal from './admissionConsultation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import CancelAppointmentWarningModal from './admissionConsultation/CancelAppointmentWarning';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { InquiryEvents } from '~/analytics/inquiryEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

export interface AppointmentProps {
  inquiryExternalId?: string;
  id: string;
  isLoading: boolean;
  appointments: AppointmentCardDisplayProps[];
  numUpcoming: number;
  inquiryStatus?: InquiryStatusValue;
  patient?: Patient;
}

interface ScheduleAppointmentConsultationCardProps {
  onScheduleClick: () => void;
}

// TODO: This component is a placeholder for the admissions consultation card. It will be moved to equip-ui in future.
const ScheduleAppointmentConsultationCard = ({
  onScheduleClick,
}: ScheduleAppointmentConsultationCardProps) => {
  return (
    <ChakraHStack
      justify="space-between"
      align="center"
      borderColor="neutral.line"
      borderRadius="12px"
      borderStyle="dashed"
      borderWidth="1px"
      padding="18px"
    >
      <ChakraVStack align="start" spacing="4px">
        <Text variant="bodyMedium" color="neutral.secondary">
          Admissions consultation
        </Text>
        <Text variant="caption" color="neutral.secondary">
          Not yet scheduled
        </Text>
      </ChakraVStack>
      <FireflyButton
        size="small"
        variant="secondary"
        id="schedule-button"
        leftIcon={FAIconName.plusRegular}
        onClick={onScheduleClick}
      >
        Schedule
      </FireflyButton>
    </ChakraHStack>
  );
};

export const Appointments = ({
  id,
  inquiryExternalId,
  appointments,
  isLoading,
  numUpcoming,
  inquiryStatus,
  patient,
}: AppointmentProps) => {
  const TOPLEVEL_ID = `${id}__appointments`;

  const {
    appointmentAdmissionsConsultation:
      isAppointmentAdmissionsConsultationEnabled,
    appointmentMaudAcReschedule: isAppointmentMaudAcRescheduleEnabled,
  } = useFlags();

  const { trackEvent } = useAnalytics();

  const [showAdmissionConsultationModal, setShowAdmissionConsultationModal] =
    useState(false);

  const [selectedAppointmentIdToCancel, setSelectedAppointmentIdToCancel] =
    useState<string>();

  const [selectedAppointmentToReschedule, setSelectedAppointmentToReschedule] =
    useState<string>();

  const canScheduleAdmissionConsultation = useMemo(() => {
    if (!inquiryExternalId || !isAppointmentAdmissionsConsultationEnabled)
      return false;
    const hadAdmissionConsultationAppointment = appointments.some(
      (appointment) =>
        appointment.title === AppointmentTypeName.ADMISSION_CONSULTATION,
    );

    if (
      inquiryStatus === InquiryStatusValue.NEW ||
      inquiryStatus === InquiryStatusValue.FIELDED ||
      inquiryStatus === InquiryStatusValue.QUALIFIED
    ) {
      return !hadAdmissionConsultationAppointment;
    }
    return false;
  }, [
    inquiryStatus,
    appointments,
    inquiryExternalId,
    patient?.userId,
    isAppointmentAdmissionsConsultationEnabled,
  ]);

  const handleScheduleClick = () => {
    trackEvent(InquiryEvents.admissionConsultationScheduleClicked, {
      inquiryId: inquiryExternalId,
      source: AnalyticsSourceValues.INQUIRY_DETAILS,
    });
    setShowAdmissionConsultationModal(true);
  };

  const handleCancelKebabOptionClick = (appointmentId: string) => {
    trackEvent(InquiryEvents.admissionConsultationCancelKebabOptionClicked, {
      inquiryId: inquiryExternalId,
      appointmentId,
      source: AnalyticsSourceValues.INQUIRY_DETAILS,
    });
    setSelectedAppointmentIdToCancel(appointmentId);
  };

  const handleEditAppointmentClick = (appointmentId: string) => {
    trackEvent(
      InquiryEvents.admissionConsultationEditAppointmentKebabOptionClicked,
      {
        inquiryId: inquiryExternalId,
        appointmentId,
        source: AnalyticsSourceValues.INQUIRY_DETAILS,
      },
    );

    setSelectedAppointmentToReschedule(appointmentId);
    setShowAdmissionConsultationModal(true);
  };

  const getMenuOptions = (appointment: AppointmentCardDisplayProps) => {
    const menuOptions = [];
    if (appointment.title === AppointmentTypeName.ADMISSION_CONSULTATION) {
      menuOptions.push({
        label: 'Cancel appointment',
        onClick: () => handleCancelKebabOptionClick(appointment.id),
        icon: <Icon name={FAIconName.trashRegular} color="critical.base" />,
      });
      if (isAppointmentMaudAcRescheduleEnabled) {
        menuOptions.push({
          label: 'Edit appointment',
          onClick: () => handleEditAppointmentClick(appointment.id),
          icon: <Icon name={FAIconName.penRegular} color="primary.100" />,
        });
      }
    }
    return menuOptions;
  };

  const appointmentCards =
    appointments.length > 0 ? (
      appointments.map((app) => {
        const tooltipWrapper = getTooltipWrapper(
          app?.assignee?.id,
          app?.dateTime.toISOString(),
        );
        return (
          <AppointmentCard
            key={app.id}
            menuOptions={getMenuOptions(app)}
            tooltipWrapper={tooltipWrapper}
            {...app}
          />
        );
      })
    ) : (
      <EmptySectionCard
        caption="No appointments have been scheduled"
        id={TOPLEVEL_ID}
      />
    );

  return (
    <Section id={TOPLEVEL_ID}>
      <InquiryDetailSectionHeader
        count={numUpcoming}
        countDescription="upcoming"
        id={`${TOPLEVEL_ID}-header`}
        title="Appointments"
      />
      <SkeletonLoader id={TOPLEVEL_ID} isLoaded={!isLoading}>
        <Section id={`${TOPLEVEL_ID}-children`}>
          <>
            {canScheduleAdmissionConsultation && (
              <ScheduleAppointmentConsultationCard
                onScheduleClick={handleScheduleClick}
              />
            )}
            {appointmentCards}
          </>
        </Section>
      </SkeletonLoader>
      {patient && inquiryExternalId && (
        <AdmissionConsultationModal
          patient={patient}
          inquiryExternalId={inquiryExternalId}
          isOpen={showAdmissionConsultationModal}
          editingAppointmentID={selectedAppointmentToReschedule}
          onClose={() => {
            setShowAdmissionConsultationModal(false);
            setSelectedAppointmentToReschedule(undefined);
          }}
        />
      )}
      {selectedAppointmentIdToCancel && inquiryExternalId && (
        <CancelAppointmentWarningModal
          isOpen={!!selectedAppointmentIdToCancel}
          onClose={() => setSelectedAppointmentIdToCancel(undefined)}
          appointmentExternalId={selectedAppointmentIdToCancel!}
          inquiryExternalId={inquiryExternalId!}
        />
      )}
    </Section>
  );
};
